import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { uploadToCloudinary } from '../../utils/cloudinaryUpload';
import RichTextEditor from '../ui/RichTextEditor';
import { 
  ArrowLeft, 
  Save, 
  Calendar, 
  DollarSign, 
  Award,
  AlertCircle,
  CheckCircle,
  FileText,
  Users,
  Globe,
  Plus,
  X,
  Upload,
  Settings,
  RefreshCw
} from 'lucide-react';
import '../../styles/CollectionDetailAdmin.css';
import '../../styles/NewSubmission.css';

const EditSubmissionDetails = () => {
  const { collectionId } = useParams();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [allOrganizations, setAllOrganizations] = useState([]);
  const [organizationTemplate, setOrganizationTemplate] = useState(null);
  const [saveTimeout, setSaveTimeout] = useState(null);
  
  // Testing mode state
  const [testers, setTesters] = useState([]);
  const [testerSearch, setTesterSearch] = useState('');
  const [testerSearchResults, setTesterSearchResults] = useState([]);
  const [searchingTesters, setSearchingTesters] = useState(false);
  const [flutterAppUrl, setFlutterAppUrl] = useState('https://vibrantcast.com');
  
  // Acceptance info fields
  const [acceptanceInfo, setAcceptanceInfo] = useState({
    date: '',
    time: '',
    attire: '',
    take_down: '',
    guest_access: '',
    display_setup: ''
  });
  
  // Dynamic form sections
  const [formSections, setFormSections] = useState([]);

  useEffect(() => {
    fetchSubmissionData();
    fetchOrganizations();
  }, [collectionId]);

  const fetchOrganizations = async () => {
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('organization_id, name, membership_fee, membership_signup_enabled')
        .order('name');
      
      if (error) throw error;
      setAllOrganizations(data || []);
    } catch (err) {
      console.error('Error fetching organizations:', err);
    }
  };

  // Helper function to format date for datetime-local input (UTC -> local)
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      // Parse the ISO/UTC date string and convert to local time for the input
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Format as YYYY-MM-DDTHH:MM (local time for the datetime-local input)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      console.warn('Invalid date format:', dateString);
      return '';
    }
  };

  // Helper to convert a local datetime-local string back to a UTC ISO string for DB storage
  const localDatetimeToUTC = (localDatetimeStr) => {
    if (!localDatetimeStr) return null;
    try {
      // datetime-local value is in the user's local timezone (e.g. "2026-04-11T23:59")
      const localDate = new Date(localDatetimeStr);
      if (isNaN(localDate.getTime())) return localDatetimeStr;
      return localDate.toISOString();
    } catch (e) {
      return localDatetimeStr;
    }
  };

  const fetchSubmissionData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('submissions_details')
        .select('*')
        .eq('collection_id', collectionId)
        .single();

      if (error) throw error;

      setSubmission(data);
      
      // Fetch organization from collection
      const { data: collectionData } = await supabase
        .from('collections')
        .select('organization_id, organizations(organization_id, name)')
        .eq('collection_id', collectionId)
        .single();
      
      if (collectionData?.organizations) {
        setOrganization(collectionData.organizations);
        
        // Load organization template if available
        const { data: templateData } = await supabase
          .from('organization_submission_templates')
          .select('*')
          .eq('organization_id', collectionData.organizations.organization_id)
          .eq('is_default', true)
          .single();
        
        if (templateData) {
          setOrganizationTemplate(templateData);
        }
      }
      
      // Parse acceptance_info JSON and populate individual fields
      let parsedAcceptanceInfo = {};
      try {
        // Handle both string and object cases
        if (typeof data.acceptance_info === 'string') {
          parsedAcceptanceInfo = JSON.parse(data.acceptance_info || '{}');
        } else if (typeof data.acceptance_info === 'object' && data.acceptance_info !== null) {
          parsedAcceptanceInfo = data.acceptance_info;
        }
      } catch (e) {
        console.warn('Invalid acceptance_info JSON:', e);
      }
      
      setAcceptanceInfo({
        date: parsedAcceptanceInfo.date || '',
        time: parsedAcceptanceInfo.time || '',
        attire: parsedAcceptanceInfo.attire || '',
        take_down: parsedAcceptanceInfo.take_down || '',
        guest_access: parsedAcceptanceInfo.guest_access || '',
        display_setup: parsedAcceptanceInfo.display_setup || ''
      });
      
      // Parse form_config JSON and populate form sections
      let parsedFormConfig = [];
      try {
        // Handle both string and object cases
        if (typeof data.form_config === 'string') {
          const formConfigData = JSON.parse(data.form_config || '[]');
          parsedFormConfig = Array.isArray(formConfigData) ? formConfigData : [];
        } else if (Array.isArray(data.form_config)) {
          parsedFormConfig = data.form_config;
        } else if (typeof data.form_config === 'object' && data.form_config !== null) {
          // If it's an object but not an array, try to convert it or use default
          parsedFormConfig = [];
        }
      } catch (e) {
        console.warn('Invalid form_config JSON:', e);
        parsedFormConfig = [];
      }
      
      // Ensure parsedFormConfig is always an array
      if (!Array.isArray(parsedFormConfig)) {
        parsedFormConfig = [];
      }
      
      // If form_config is empty and we have a template, use template fields
      if (parsedFormConfig.length === 0 && templateData?.field_definitions) {
        const templateFields = typeof templateData.field_definitions === 'string'
          ? JSON.parse(templateData.field_definitions)
          : templateData.field_definitions;
        
        if (Array.isArray(templateFields)) {
          parsedFormConfig = templateFields;
        }
      }
      
      setFormSections(parsedFormConfig);
      
      setFormData({
        title: data.title || '',
        theme: data.theme || '',
        callout_headline: data.callout_headline || '',
        open_date: formatDateForInput(data.open_date),
        submission_deadline: formatDateForInput(data.submission_deadline),
        submission_price: data.submission_price || '',
        max_allowed: data.max_allowed || 1,
        submission_guidelines: data.submission_guidelines || '',
        artistic_guidelines: data.artistic_guidelines || '',
        important_dates: data.important_dates || '',
        promotion_guidelines: data.promotion_guidelines || '',
        acceptance_info: JSON.stringify(parsedAcceptanceInfo, null, 2),
        form_config: JSON.stringify(parsedFormConfig, null, 2),
        cover_image: data.cover_image || '',
        featured: data.featured || false,
        member_free: data.member_free || false,
        organization_id: data.organization_id || null,
        member_access_type: data.member_access_type || 'open',
        member_submission_fee: data.member_submission_fee || '',
        has_video: data.has_video || false,
        external_url: data.external_url || '',
        mandrill_template: data.mandrill_template || '',
        accepted_message: data.accepted_message || '',
        acceptance_terms: data.acceptance_terms || '',
        status: data.status || 'draft',
        test_token: data.test_token || ''
      });
      
      // Parse testers array
      try {
        const parsedTesters = typeof data.testers === 'string' 
          ? JSON.parse(data.testers || '[]') 
          : (data.testers || []);
        setTesters(Array.isArray(parsedTesters) ? parsedTesters : []);
      } catch (e) {
        console.warn('Invalid testers JSON:', e);
        setTesters([]);
      }
    } catch (err) {
      console.error('Error fetching submission data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getDefaultFormConfig = () => ({
    required_fields: ["artist_name", "email"],
    optional_fields: ["phone", "website", "instagram"],
    sections: {
      artist_statement: {
        enabled: true,
        required: false,
        max_length: 200,
        placeholder: "Describe your artistic practice..."
      },
      professional_info: {
        enabled: true,
        required: false,
        fields: {
          preferred_mediums: {
            enabled: true,
            required: false,
            options: ["Painting", "Sculpture", "Photography", "Digital Art", "Mixed Media", "Printmaking", "Drawing", "Installation", "Video", "Performance"]
          },
          years_experience: {
            enabled: true,
            required: false,
            options: ["0-2", "3-5", "6-10", "11-15", "16+"]
          },
          education: {
            enabled: true,
            required: false,
            max_length: 500
          }
        }
      },
      awards: {
        enabled: true,
        required: false,
        max_count: 5,
        label: "Awards & Recognition"
      },
      exhibitions: {
        enabled: true,
        required: false,
        max_count: 5,
        label: "Notable Exhibitions"
      }
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setUploadingImage(true);
      const result = await uploadToCloudinary(file, 'image', 'submission_covers');
      handleInputChange('cover_image', result.url);
    } catch (err) {
      console.error('Error uploading image:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const validateJSON = (jsonString) => {
    // Empty or undefined is valid (will be treated as empty object/array)
    if (!jsonString || jsonString.trim() === '') {
      return true;
    }
    try {
      JSON.parse(jsonString);
      return true;
    } catch {
      return false;
    }
  };

  // Handle acceptance info field updates
  const handleAcceptanceInfoChange = (field, value) => {
    const updatedAcceptanceInfo = {
      ...acceptanceInfo,
      [field]: value
    };
    setAcceptanceInfo(updatedAcceptanceInfo);
    
    // Update the JSON in formData
    setFormData(prev => ({
      ...prev,
      acceptance_info: JSON.stringify(updatedAcceptanceInfo, null, 2)
    }));
  };

  // Handle form section management
  const addFormSection = () => {
    const currentSections = Array.isArray(formSections) ? formSections : [];
    const newSection = {
      id: Date.now().toString(),
      type: 'text',
      label: 'New Field',
      required: false,
      placeholder: ''
    };
    const updatedSections = [...currentSections, newSection];
    setFormSections(updatedSections);
    updateFormConfig(updatedSections);
  };

  const removeFormSection = (index) => {
    if (!Array.isArray(formSections)) return;
    
    // Confirm before deleting
    const fieldLabel = formSections[index]?.label || `Field ${index + 1}`;
    if (!window.confirm(`Are you sure you want to delete "${fieldLabel}"? This action cannot be undone.`)) {
      return;
    }
    
    const updatedSections = formSections.filter((_, i) => i !== index);
    setFormSections(updatedSections);
    updateFormConfig(updatedSections);
  };

  const updateFormSection = (index, field, value) => {
    if (!Array.isArray(formSections)) return;
    const updatedSections = formSections.map((section, i) => 
      i === index ? { ...section, [field]: value } : section
    );
    setFormSections(updatedSections);
    updateFormConfig(updatedSections);
  };

  // Tester search functionality
  const searchTesters = async (query) => {
    if (query.length < 2) {
      setTesterSearchResults([]);
      return;
    }
    
    setSearchingTesters(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('uuid, first_name, last_name, nickname, email')
        .or(`email.ilike.%${query}%,nickname.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
        .limit(10);
      
      if (error) throw error;
      
      // Filter out users already in testers list and add display_name
      const existingIds = testers.map(t => t.uuid);
      const filtered = (data || [])
        .filter(u => !existingIds.includes(u.uuid))
        .map(u => ({
          ...u,
          display_name: u.nickname || `${u.first_name || ''} ${u.last_name || ''}`.trim() || null
        }));
      setTesterSearchResults(filtered);
    } catch (err) {
      console.error('Error searching testers:', err);
      setTesterSearchResults([]);
    } finally {
      setSearchingTesters(false);
    }
  };

  const addTester = (user) => {
    setTesters(prev => [...prev, {
      uuid: user.uuid,
      email: user.email,
      display_name: user.display_name,
      added_at: new Date().toISOString()
    }]);
    setTesterSearch('');
    setTesterSearchResults([]);
  };

  const removeTester = (uuid) => {
    setTesters(prev => prev.filter(t => t.uuid !== uuid));
  };

  const generateTestToken = () => {
    const token = crypto.randomUUID();
    handleInputChange('test_token', token);
  };

  const copyTestUrl = () => {
    const submissionId = submission?.submission_id || collectionId;
    // Use Cloudflare worker URL for proper metadata/OG tags
    const testUrl = `https://share.vibrantcast.com/submission?submissionId=${submissionId}&test=${formData.test_token}`;
    navigator.clipboard.writeText(testUrl);
    alert('Test URL copied to clipboard!');
  };

  const syncWithTemplate = async () => {
    if (!organization?.organization_id) {
      alert('No organization linked to this submission');
      return;
    }

    try {
      // Fetch the LATEST template from database (not using stale cached state)
      const { data: latestTemplate, error: fetchError } = await supabase
        .from('organization_submission_templates')
        .select('*')
        .eq('organization_id', organization.organization_id)
        .eq('is_default', true)
        .single();

      if (fetchError || !latestTemplate?.field_definitions) {
        alert('No template found for this organization');
        return;
      }

      // Update the cached template state with fresh data
      setOrganizationTemplate(latestTemplate);

      const templateFields = typeof latestTemplate.field_definitions === 'string'
        ? JSON.parse(latestTemplate.field_definitions)
        : latestTemplate.field_definitions;

      if (!Array.isArray(templateFields)) {
        alert('Template has invalid field definitions');
        return;
      }

      // Smart merge: update field metadata from template but preserve existing values
      const currentFieldsMap = new Map();
      if (Array.isArray(formSections)) {
        formSections.forEach(field => {
          if (field.id) {
            currentFieldsMap.set(field.id, field);
          }
        });
      }

      // Merge template fields with existing values
      const mergedFields = templateFields.map(templateField => {
        const existingField = currentFieldsMap.get(templateField.id);
        if (existingField) {
          // Update structure from template, but preserve the value
          return {
            ...templateField,
            value: existingField.value || templateField.value || ''
          };
        }
        // New field from template - use template defaults
        return { ...templateField };
      });

      setFormSections(mergedFields);
      // Update formData immediately instead of using debounced updateFormConfig
      setFormData(prev => ({
        ...prev,
        form_config: JSON.stringify(mergedFields, null, 2)
      }));

      alert('Form synced with latest template! Field types and labels updated. Values preserved. Click Save to persist changes.');
    } catch (err) {
      console.error('Error syncing with template:', err);
      alert('Error syncing with template: ' + err.message);
    }
  };

  const updateFormConfig = (sections) => {
    // Debounce to prevent infinite loops
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    
    const timeout = setTimeout(() => {
      setFormData(prev => ({
        ...prev,
        form_config: JSON.stringify(sections, null, 2)
      }));
    }, 300);
    
    setSaveTimeout(timeout);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Validate form_config JSON if provided (acceptance_info is optional legacy field)
      if (formData.form_config && formData.form_config.trim() !== '' && !validateJSON(formData.form_config)) {
        throw new Error('Form Config must be valid JSON');
      }

      // Helper to safely parse JSON with fallback
      const safeParseJSON = (jsonString, fallback = {}) => {
        if (!jsonString || jsonString.trim() === '') return fallback;
        try {
          return JSON.parse(jsonString);
        } catch {
          return fallback;
        }
      };

      const updateData = {
        ...formData,
        acceptance_info: safeParseJSON(formData.acceptance_info, {}),
        form_config: safeParseJSON(formData.form_config, []),
        submission_price: formData.submission_price ? parseFloat(formData.submission_price) : null,
        max_allowed: parseInt(formData.max_allowed) || 1,
        // Convert local datetime-local strings to UTC ISO strings for PostgreSQL timestamptz fields
        open_date: localDatetimeToUTC(formData.open_date),
        submission_deadline: localDatetimeToUTC(formData.submission_deadline),
        // Organization membership fields
        organization_id: formData.organization_id || null,
        member_access_type: formData.member_access_type || 'open',
        member_submission_fee: formData.member_submission_fee ? parseFloat(formData.member_submission_fee) : null,
        // Status and testing fields
        status: formData.status || 'draft',
        test_token: formData.test_token || null,
        testers: testers
      };

      const { error } = await supabase
        .from('submissions_details')
        .update(updateData)
        .eq('collection_id', collectionId);

      if (error) throw error;

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
    } catch (err) {
      console.error('Error saving submission details:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="collection-detail-admin">
        <div className="loading-state">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <h3>Loading Submission Details...</h3>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-detail-admin">
        <div className="error-state">
          <AlertCircle className="error-icon" />
          <h3>Error Loading Submission Details</h3>
          <p>{error}</p>
          <button onClick={() => navigate(-1)} className="btn-secondary">
            <ArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="collection-detail-admin">
      {/* Header */}
      <div className="detail-header">
        <div className="header-left">
          <button 
            onClick={() => navigate(`/curator/collections/${collectionId}?tab=submissions`)}
            className="btn-secondary"
          >
            <ArrowLeft /> Back to Submission
          </button>
          <div className="header-info">
            <h1>Edit Submission Details</h1>
            <p>Configure submission settings and form options</p>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleSave}
            disabled={saving}
            className="btn-primary"
          >
            {saving ? (
              <>
                <div className="loading-spinner"></div>
                Saving...
              </>
            ) : (
              <>
                <Save /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success">
          <CheckCircle />
          <span>Submission details updated successfully!</span>
        </div>
      )}

      {error && (
        <div className="alert alert-error">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      <div className="edit-form">
        <div className="form-section">
          <h2>Basic Information</h2>
          
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Submission title"
            />
          </div>

          <div className="form-group">
            <label>Cover Image</label>
            <div className="cover-image-row">
              {formData.cover_image && (
                <div className="cover-image-container">
                  <img src={formData.cover_image} alt="Cover" className="cover-preview-small" />
                </div>
              )}
              <div className="cover-upload-controls">
                <label className="upload-btn">
                  <Upload /> Upload New
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <h2>Submission Settings</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label>Open Date (Optional)</label>
              <input
                type="datetime-local"
                value={formData.open_date}
                onChange={(e) => handleInputChange('open_date', e.target.value)}
              />
              <small className="field-note">When this submission becomes visible to the public. Leave empty to make it visible immediately.</small>
            </div>

            <div className="form-group">
              <label>Submission Deadline</label>
              <input
                type="datetime-local"
                value={formData.submission_deadline}
                onChange={(e) => handleInputChange('submission_deadline', e.target.value)}
              />
              <small className="field-note">Defaults to 11:59 PM if no time is specified</small>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Max Submissions per Artist</label>
              <input
                type="number"
                min="1"
                value={formData.max_allowed}
                onChange={(e) => handleInputChange('max_allowed', e.target.value)}
              />
            </div>
            
            <div className="form-group">
              <label>Submission Price ($)</label>
              <div className="price-input-wrapper">
                <input
                  type="number"
                  step="0.01"
                  value={formData.submission_price}
                  onChange={(e) => handleInputChange('submission_price', e.target.value)}
                  placeholder="0.00"
                />
                {formData.member_free && formData.submission_price > 0 && (
                  <span className="price-waived-badge">Free for Members</span>
                )}
              </div>
            </div>
          </div>

          {/* Organization-based Member Access Controls */}
          <div className="member-access-section">
            <h3>Member Access Settings</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Linked Organization</label>
                <select
                  value={formData.organization_id || ''}
                  onChange={(e) => handleInputChange('organization_id', e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">No organization linked</option>
                  {allOrganizations.map(org => (
                    <option key={org.organization_id} value={org.organization_id}>
                      {org.name} {org.membership_fee ? `($${org.membership_fee}/yr)` : ''}
                    </option>
                  ))}
                </select>
                <small className="field-note">Link to an organization to enable member-specific access and pricing</small>
              </div>

              <div className="form-group">
                <label>Member Access Type</label>
                <select
                  value={formData.member_access_type}
                  onChange={(e) => handleInputChange('member_access_type', e.target.value)}
                  disabled={!formData.organization_id}
                >
                  <option value="open">Open to All</option>
                  <option value="member_discount">Members Get Discount</option>
                  <option value="member_only">Members Only</option>
                </select>
                <small className="field-note">
                  {formData.member_access_type === 'open' && 'Anyone can submit at the standard price'}
                  {formData.member_access_type === 'member_discount' && 'Members pay reduced fee, non-members pay standard price'}
                  {formData.member_access_type === 'member_only' && 'Only verified organization members can submit'}
                </small>
              </div>
            </div>

            {formData.organization_id && formData.member_access_type !== 'open' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Member Submission Fee ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.member_submission_fee}
                    onChange={(e) => handleInputChange('member_submission_fee', e.target.value)}
                    placeholder="0.00 = Free for members"
                  />
                  <small className="field-note">
                    Fee for organization members. Set to 0 for free member submissions.
                    {formData.member_access_type === 'member_discount' && formData.submission_price > 0 && (
                      <> Non-members pay ${formData.submission_price}.</>
                    )}
                  </small>
                </div>
              </div>
            )}

            {/* Legacy member_free toggle for backward compatibility */}
            <div className="member-fee-waiver-option">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={formData.member_free}
                  onChange={(e) => handleInputChange('member_free', e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
              <div className="toggle-content">
                <span className="toggle-label">Legacy: Free for Members</span>
                <span className="toggle-description">Original member free flag (use Member Access Settings above for new submissions)</span>
              </div>
            </div>
          </div>

          {/* Status & Testing Section */}
          <div className="member-access-section" style={{ marginTop: '24px' }}>
            <h3>🧪 Status & Testing</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Submission Status</label>
                <select
                  value={formData.status || 'draft'}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <option value="draft">Draft - Hidden from all users</option>
                  <option value="testing">Testing - Visible only to testers with token</option>
                  <option value="live">Live - Visible to all users</option>
                  <option value="closed">Closed - Submissions ended</option>
                </select>
                <small className="field-note">
                  {formData.status === 'draft' && 'Submission is hidden. Use for setup before publishing.'}
                  {formData.status === 'testing' && 'Only testers with the test URL can see and test this submission.'}
                  {formData.status === 'live' && 'Visible to all users in Artist Call Outs.'}
                  {formData.status === 'closed' && 'Submission has ended. Hidden from new submissions list.'}
                </small>
              </div>
            </div>

            {formData.status === 'testing' && (
              <>
                <div className="form-row">
                  <div className="form-group">
                    <label>Flutter App URL</label>
                    <input
                      type="text"
                      value={flutterAppUrl}
                      onChange={(e) => setFlutterAppUrl(e.target.value)}
                      placeholder="https://vibrantcast.com"
                    />
                    <small className="field-note">Production: https://vibrantcast.com • Local: http://localhost:XXXXX</small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Test Token</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        value={formData.test_token || ''}
                        onChange={(e) => handleInputChange('test_token', e.target.value)}
                        placeholder="Click Generate to create token"
                        style={{ flex: 1 }}
                        readOnly
                      />
                      <button 
                        type="button" 
                        onClick={generateTestToken}
                        className="btn-secondary"
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        Generate
                      </button>
                      {formData.test_token && (
                        <button 
                          type="button" 
                          onClick={copyTestUrl}
                          className="btn-secondary"
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          Copy URL
                        </button>
                      )}
                    </div>
                    {formData.test_token && (
                      <small className="field-note" style={{ marginTop: '8px', display: 'block' }}>
                        Test URL: <code style={{ fontSize: '11px', background: '#f0f0f0', padding: '2px 6px', borderRadius: '3px' }}>
                          https://share.vibrantcast.com/submission?submissionId={submission?.submission_id || collectionId}&test={formData.test_token}
                        </code>
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-group" style={{ marginTop: '16px' }}>
                  <label>Testers</label>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                    <input
                      type="text"
                      value={testerSearch}
                      onChange={(e) => {
                        setTesterSearch(e.target.value);
                        searchTesters(e.target.value);
                      }}
                      placeholder="Search by email or name..."
                      style={{ flex: 1 }}
                    />
                    {searchingTesters && <span style={{ padding: '8px' }}>Searching...</span>}
                  </div>
                  
                  {/* Search results dropdown */}
                  {testerSearchResults.length > 0 && (
                    <div style={{ 
                      border: '1px solid #ddd', 
                      borderRadius: '4px', 
                      marginBottom: '12px',
                      maxHeight: '200px',
                      overflowY: 'auto'
                    }}>
                      {testerSearchResults.map(user => (
                        <div 
                          key={user.uuid}
                          onClick={() => addTester(user)}
                          style={{ 
                            padding: '8px 12px', 
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <div>
                            <strong>{user.display_name || 'No name'}</strong>
                            <br />
                            <small style={{ color: '#666' }}>{user.email}</small>
                          </div>
                          <Plus size={16} color="#16a34a" />
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Current testers list */}
                  {testers.length > 0 ? (
                    <div style={{ 
                      border: '1px solid #ddd', 
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      {testers.map((tester, idx) => (
                        <div 
                          key={tester.uuid}
                          style={{ 
                            padding: '10px 12px', 
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: idx < testers.length - 1 ? '1px solid #eee' : 'none',
                            backgroundColor: '#fafafa'
                          }}
                        >
                          <div>
                            <strong>{tester.display_name || tester.email}</strong>
                            {tester.display_name && <small style={{ color: '#666', marginLeft: '8px' }}>{tester.email}</small>}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeTester(tester.uuid)}
                            style={{
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <X size={16} color="#dc2626" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                      No testers added yet. Search for users above to add them.
                    </p>
                  )}
                  <small className="field-note" style={{ marginTop: '8px', display: 'block' }}>
                    Testers can access this submission using the test URL while in Testing status.
                  </small>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="form-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div>
              <h2 style={{ margin: 0 }}>Submission Details</h2>
              <p className="section-description" style={{ margin: '4px 0 0 0' }}>Configure the fields shown on the public submission page. These come from your organization template.</p>
            </div>
            {organizationTemplate && (
              <button 
                type="button"
                onClick={syncWithTemplate}
                className="btn-secondary"
                style={{ marginLeft: '16px', whiteSpace: 'nowrap' }}
                title="Sync with latest template"
              >
                <RefreshCw size={16} />
                Sync with Template
              </button>
            )}
          </div>
          
          <div className="form-sections-container">
            {Array.isArray(formSections) && formSections.map((section, index) => (
              <div key={section.id || index} className="form-section-item" style={{
                opacity: section.enabled === false ? 0.6 : 1,
                backgroundColor: section.enabled === false ? '#f9fafb' : 'white'
              }}>
                {/* Field Title - Large, updates as you type */}
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    value={section.label || ''}
                    onChange={(e) => updateFormSection(index, 'label', e.target.value)}
                    placeholder="Field Title"
                    style={{
                      fontSize: '20px',
                      fontWeight: '600',
                      border: 'none',
                      borderBottom: '2px solid #e5e7eb',
                      padding: '8px 0',
                      width: '100%',
                      backgroundColor: 'transparent'
                    }}
                  />
                </div>

                {/* Field Controls Row */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'center',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Enabled Toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={section.enabled !== false}
                        onChange={(e) => updateFormSection(index, 'enabled', e.target.checked)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>Visible</span>
                    </label>

                    {/* Required Toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={section.required || false}
                        onChange={(e) => updateFormSection(index, 'required', e.target.checked)}
                        disabled={section.enabled === false}
                        style={{ width: '16px', height: '16px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500', color: section.enabled === false ? '#9ca3af' : 'inherit' }}>Required</span>
                    </label>

                    {/* Field Type Selector */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6b7280' }}>Type:</span>
                      <select
                        value={section.type || 'text'}
                        onChange={(e) => updateFormSection(index, 'type', e.target.value)}
                        disabled={section.enabled === false}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          border: '1px solid #e5e7eb',
                          fontSize: '14px',
                          backgroundColor: section.enabled === false ? '#f3f4f6' : 'white'
                        }}
                      >
                        <option value="text">Text Input</option>
                        <option value="textarea">Text Area</option>
                        <option value="html">Rich Text/HTML</option>
                        <option value="date">Date</option>
                        <option value="datetime">Date & Time</option>
                        <option value="email">Email</option>
                        <option value="phone">Phone</option>
                        <option value="number">Number</option>
                        <option value="select">Dropdown</option>
                        <option value="checkbox">Checkbox</option>
                        <option value="radio">Radio Buttons</option>
                      </select>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <button
                    type="button"
                    onClick={() => removeFormSection(index)}
                    className="btn-danger-small"
                    title="Delete field"
                  >
                    <X size={16} />
                  </button>
                </div>
                
                {/* Date field */}
                {section.type === 'date' && (
                  <div className="form-group">
                    <input
                      type="date"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                    />
                  </div>
                )}
                
                {/* Datetime field */}
                {section.type === 'datetime' && (
                  <div className="form-group">
                    <input
                      type="datetime-local"
                      value={section.value ? formatDateForInput(section.value) : ''}
                      onChange={(e) => {
                        // Convert to ISO 8601 format with timezone
                        const localDate = new Date(e.target.value);
                        const isoString = localDate.toISOString();
                        updateFormSection(index, 'value', isoString);
                      }}
                    />
                  </div>
                )}
                
                {/* HTML/Rich text field */}
                {section.type === 'html' && (
                  <div className="form-group">
                    <RichTextEditor
                      value={section.value || ''}
                      onChange={(value) => updateFormSection(index, 'value', value)}
                      placeholder="Enter rich text content..."
                      minHeight="150px"
                      enableImageUpload={true}
                      imageFolder="submission_content"
                    />
                  </div>
                )}

                {/* Text field */}
                {section.type === 'text' && (
                  <div className="form-group">
                    <input
                      type="text"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}

                {/* Textarea field */}
                {section.type === 'textarea' && (
                  <div className="form-group">
                    <textarea
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                      rows="4"
                    />
                  </div>
                )}

                {/* Email field */}
                {section.type === 'email' && (
                  <div className="form-group">
                    <input
                      type="email"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}

                {/* Phone field */}
                {section.type === 'phone' && (
                  <div className="form-group">
                    <input
                      type="tel"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}

                {/* Number field */}
                {section.type === 'number' && (
                  <div className="form-group">
                    <input
                      type="number"
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                      placeholder={section.placeholder || ''}
                    />
                  </div>
                )}

                {/* Select field */}
                {section.type === 'select' && (
                  <div className="form-group">
                    <select
                      value={section.value || ''}
                      onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                    >
                      <option value="">Select an option...</option>
                      {section.options?.map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Radio field */}
                {section.type === 'radio' && (
                  <div className="form-group">
                    {section.options?.map((option, i) => (
                      <label key={i} style={{ display: 'block', marginBottom: '8px' }}>
                        <input
                          type="radio"
                          name={`radio-${section.id}`}
                          value={option}
                          checked={section.value === option}
                          onChange={(e) => updateFormSection(index, 'value', e.target.value)}
                          style={{ marginRight: '8px' }}
                        />
                        {option}
                      </label>
                    ))}
                  </div>
                )}

                {/* Checkbox field */}
                {section.type === 'checkbox' && (
                  <div className="form-group">
                    <label style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={section.value === true || section.value === 'true'}
                        onChange={(e) => updateFormSection(index, 'value', e.target.checked)}
                        style={{ marginRight: '8px' }}
                      />
                      {section.helpText || 'Check to enable'}
                    </label>
                  </div>
                )}

                {/* Placeholder config for text-based fields */}
                {!['date', 'datetime', 'html', 'select', 'radio', 'checkbox'].includes(section.type) && (
                  <div className="form-group" style={{ marginTop: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#6b7280' }}>Placeholder Text (shown to artists)</label>
                    <input
                      type="text"
                      value={section.placeholder || ''}
                      onChange={(e) => updateFormSection(index, 'placeholder', e.target.value)}
                      placeholder="Enter placeholder text..."
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                )}

                {/* Options config for select/radio */}
                {(section.type === 'select' || section.type === 'radio') && (
                  <div className="form-group">
                    <label>Options (one per line)</label>
                    <textarea
                      value={section.options ? section.options.join('\n') : ''}
                      onChange={(e) => updateFormSection(index, 'options', e.target.value.split('\n').filter(opt => opt.trim()))}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.max(e.target.scrollHeight, 80) + 'px';
                      }}
                      placeholder="Option 1&#10;Option 2&#10;Option 3"
                      style={{ resize: 'none' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div style={{ marginTop: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
            <p style={{ margin: 0, fontStyle: 'italic', color: '#666' }}>
              To add or remove fields, use the template manager.
            </p>
            <button 
              className="btn-secondary"
              onClick={() => navigate('/curator/submission-template')}
              style={{ marginLeft: '16px' }}
            >
              <Settings size={16} />
              Manage Template
            </button>
          </div>
        </div>

        <div className="form-section">
          <h2><FileText size={20} /> Acceptance Terms</h2>
          <p className="section-description">
            The full Artist Exhibition Agreement shown when artists accept their invitation. 
            Artists must agree to these terms before confirming participation.
          </p>
          
          <div className="form-group">
            <label>Agreement Content</label>
            <RichTextEditor
              value={formData.acceptance_terms || ''}
              onChange={(html) => handleInputChange('acceptance_terms', html)}
              placeholder="Enter the artist exhibition agreement terms..."
              minHeight="200px"
            />
            {!formData.acceptance_terms && (
              <small className="field-note" style={{ color: '#d97706', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '8px' }}>
                <AlertCircle size={14} />
                No acceptance terms set. Artists will not see an agreement section on the acceptance page.
              </small>
            )}
          </div>
        </div>
      </div>

      {/* Sticky bottom action bar */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        backgroundColor: 'white',
        borderTop: '1px solid #e2e8f0',
        padding: '12px 24px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: '12px',
        zIndex: 100,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
        maxWidth: '100%'
      }}>
        {success && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#16a34a', fontSize: '14px', fontWeight: '500', marginRight: 'auto' }}>
            <CheckCircle size={16} /> Saved!
          </span>
        )}
        {submission?.submission_id && (
          <a
            href={`https://www.vibrantcast.com/artist/submissions/${submission.submission_id}?submissionId=${submission.submission_id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', maxWidth: '250px' }}
          >
            <Globe size={16} /> Preview Page
          </a>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
          style={{ maxWidth: '250px', width: 'auto', flex: 'none' }}
        >
          {saving ? (
            <>
              <div className="loading-spinner"></div>
              Saving...
            </>
          ) : (
            <>
              <Save size={16} /> Save Changes
            </>
          )}
        </button>
      </div>

      {/* Spacer so content isn't hidden behind sticky bar */}
      <div style={{ height: '70px' }} />
    </div>
  );
};

export default EditSubmissionDetails;
